import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useMessages from '../hooks/useMessages';
import { useChat } from '../context/ChatProvider';
import MessageContainer from './MessageContainer';
import { MessageContent } from './MessageContent';
import { languageConfig } from '../context/LanguageConfig';
import { BeatLoader } from "react-spinners";
import { MdTranslate } from "react-icons/md";

const ConversationContainer = styled.div`
    display: flex;
    flex-direction: column-reverse;
    gap: 0.4rem;
    flex: 1;
    padding: 1.2rem 1rem;
    overflow: auto;
    overflow-y: auto;

    /* custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

const UserProfile = styled.div`
    display: flex;
    position: relative;
    height: 100%;
    align-items: end;
    padding-bottom: 5px;

    &::before {
        content: '';
        display: block;
        width: 2em;
        height: 2em;
        border-radius: 50%;
        background: ${props => props.isImage ? `url(${props.content}) center/cover no-repeat` : 'rgba(99, 102, 241, 0.3)'};
        border: ${props => props.isImage ? '2px solid rgba(255,255,255,0.1)' : 'none'};
        flex-shrink: 0;
    }
`;

const BotMessage = styled.div`
    width: fit-content;
    margin: 0.4rem auto;
    padding: 0.5em 1.4em;
    font-size: 0.72em;
    text-align: center;
    border-radius: 2em;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.45);
    backdrop-filter: blur(8px);
`;

const Message = styled.div`
    display: flex;
    flex-direction: column-reverse;
    align-items: ${props => props.incomingMessage ? 'flex-start' : 'flex-end'};
    margin-bottom: 0.3em;
`;

const MessageRow = styled.div`
    display: flex;
    align-items: flex-end;
    transition: .3s ease-in-out all;
`;

const MessageStack = styled.div`
    display: flex;
    flex-direction: column-reverse;
    gap: 2px;
`;

const MessageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.65em;
    margin-bottom: 3px;
`;

const AuthorName = styled.span`
    margin-right: auto;
    color: rgba(255, 255, 255, 0.4);
    font-weight: 500;
`;

const TimeStamp = styled.span`
    margin-left: 6px;
    margin-right: 6px;
    align-self: flex-end;
    color: rgba(255, 255, 255, 0.25);
    font-size: 0.6em;
    white-space: nowrap;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;

const RomanizeToggle = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.3);
    font-size: 0.7em;
    padding: 0 4px;
    display: flex;
    align-items: center;
    gap: 2px;
    transition: color 0.2s;
    align-self: ${props => props.incomingMessage ? 'flex-start' : 'flex-end'};
    margin: 0 4px;

    &:hover {
        color: rgba(255, 255, 255, 0.6);
    }
`;

const ROMANIZED_SUFFIX = ' (Romanized)';

// Separate translations into base languages and their romanized pairs
const getTranslationEntries = (translations, userLanguage) => {
    const romanizedMap = {};
    const baseEntries = [];

    // First pass: identify romanized entries
    for (const [key, value] of Object.entries(translations)) {
        if (key.endsWith(ROMANIZED_SUFFIX)) {
            const baseLang = key.slice(0, -ROMANIZED_SUFFIX.length);
            romanizedMap[baseLang] = value;
        }
    }

    // Second pass: build base entries, skipping romanized keys and user's own language
    for (const [language, translation] of Object.entries(translations)) {
        if (language === userLanguage || language.endsWith(ROMANIZED_SUFFIX)) continue;
        baseEntries.push({
            language,
            translation,
            romanized: romanizedMap[language] || null,
        });
    }

    return baseEntries;
};

// Wrapper for a translation bubble that may have a romanized toggle
const ToggleableTranslation = ({ entry, id, messages, incomingMessage, voiceId, type, generateTtsRequest, isFirst, isLast }) => {
    const [showRomanized, setShowRomanized] = useState(false);
    const { language, translation, romanized } = entry;
    const displayText = (showRomanized && romanized) ? romanized : translation;

    return (
        <>
            <MessageContent
                text={displayText}
                key={`${id}-${language}-${messages.length}`}
                messageKey={`${id}-${language}`}
                incomingMessage={incomingMessage}
                messageColor={languageConfig[language]?.color ?? 'rgba(99, 102, 241, 0.2)'}
                textColor={languageConfig[language]?.textColor ?? 'rgba(255,255,255,0.7)'}
                isTranslation
                voiceId={voiceId}
                isLast={isLast && !romanized}
                isFirst={isFirst}
                showAudio={type !== 'temp'}
                onPlayAudio={generateTtsRequest}
            />
            {romanized && (
                <RomanizeToggle
                    incomingMessage={incomingMessage}
                    onClick={() => setShowRomanized(!showRomanized)}
                    title={showRomanized ? 'Show characters' : 'Show romanized'}
                >
                    <MdTranslate size={12} />
                    {showRomanized ? language : `${language} (Romanized)`}
                </RomanizeToggle>
            )}
        </>
    );
};

const Conversation = () => {
    const { socket, user, loadingMessages } = useChat();
    const { messages } = useMessages();
    const chatConversation = useRef(null);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

    useEffect(() => {
        chatConversation.current.scrollTo(0, chatConversation.current.scrollHeight);
    }, [messages]);

    const generateTtsRequest = async (data) => {
        socket.emit('generate-tts', data);
        setCurrentlyPlaying(data.messageKey);
    };

    return (
        <ConversationContainer ref={chatConversation}>
            {loadingMessages ? (
                <LoadingContainer>
                    <BeatLoader color="#a5b4fc" loading={loadingMessages} size={12} />
                </LoadingContainer>
            ) : (
                [...messages].reverse().map((m, index) => {
                    const { author, author_language, author_avatar, id, translations, time, voiceId, type } = m;
                    const isBot = author === 'BOT';
                    const incomingMessage = author !== user.username;

                    return isBot ? (
                        <BotMessage key={`bot-${index}`}>{m.text}</BotMessage>
                    ) : (
                        <MessageContainer key={`message-${index}-${messages.length}`} incomingMessage={incomingMessage}>
                            <UserProfile isImage={!isBot} content={author_avatar} />
                            <Message incomingMessage={incomingMessage}>
                                <MessageRow>
                                    {!incomingMessage && <TimeStamp>{new Date(time).toLocaleTimeString()}</TimeStamp>}
                                    <MessageStack>
                                        <MessageContent
                                            key={`${id}-${messages.length}`}
                                            text={m.text}
                                            incomingMessage={incomingMessage}
                                            messageColor={languageConfig[author_language]?.color ?? 'rgba(99, 102, 241, 0.3)'}
                                            textColor={languageConfig[author_language]?.textColor ?? 'rgba(255,255,255,0.9)'}
                                            clickedMessage={!m.type ?? true}
                                            messageType={type}
                                        />
                                        {getTranslationEntries(translations, user.language)
                                            .map((entry, i, array) => (
                                                <ToggleableTranslation
                                                    key={`${id}-${entry.language}-${messages.length}`}
                                                    entry={entry}
                                                    id={id}
                                                    messages={messages}
                                                    incomingMessage={incomingMessage}
                                                    voiceId={voiceId}
                                                    type={type}
                                                    generateTtsRequest={generateTtsRequest}
                                                    isFirst={i === 0}
                                                    isLast={i === array.length - 1}
                                                />
                                            ))}
                                    </MessageStack>
                                    {incomingMessage && !m.type && <TimeStamp>{new Date(time).toLocaleTimeString()}</TimeStamp>}
                                </MessageRow>
                                <MessageHeader>
                                    {incomingMessage && <AuthorName>{author}</AuthorName>}
                                </MessageHeader>
                            </Message>
                        </MessageContainer>
                    );
                })
            )}
        </ConversationContainer>
    );
};

export default Conversation;
